import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import KloneDiscoverCTA from './KloneDiscoverCTA';
import { getUtm, trackCta } from './tracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'ff_lokal_form_draft';

const WINE_CATEGORIES = [
  { value: 'red', label: 'Red' },
  { value: 'white', label: 'White' },
  { value: 'rose', label: 'Rosé' },
  { value: 'fruit', label: 'Fruit wine' },
  { value: 'cider', label: 'Cider' },
  { value: 'not_sure', label: 'Not sure — recommend' },
];

const SWEETNESS = [
  { value: 'dry', label: 'Dry' },
  { value: 'medium', label: 'Medium' },
  { value: 'sweet', label: 'Sweet' },
  { value: 'not_sure', label: 'Not sure' },
];

const CONTACT_METHOD = [
  { value: 'phone', label: 'Phone' },
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
];

const INITIAL = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  preferredContact: 'email',
  preferredContactTime: '',
  ageConfirmed: false,
  contactConsent: false,
  bottleQuantity: 30,
  wineCategory: 'red',
  sweetness: 'not_sure',
  wineTier: 'silver',
  wineName: '',
  includeBottles: false,
  specificWine: '',
  flavourNotes: '',
  bottlesRequired: false,
  comments: '',
  incomingReferralCode: '',
};

const HALF_BATCH_TIER = 'silver';

const genIdempotencyKey = () => `ff-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const CampaignForm = ({ basePath = '' }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialQty = Number(params.get('qty')) === 15 ? 15 : 30;
  const referralIn = (params.get('ref') || sessionStorage.getItem('ff_lokal_ref_in') || '').toUpperCase();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState({});
  const [cfg, setCfg] = useState(null);
  const [idempotencyKey] = useState(genIdempotencyKey);

  const [form, setForm] = useState(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    const base = saved ? { ...INITIAL, ...JSON.parse(saved) } : INITIAL;
    return {
      ...base,
      bottleQuantity: initialQty,
      incomingReferralCode: referralIn || base.incomingReferralCode || '',
    };
  });

  useEffect(() => { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(form)); }, [form]);

  useEffect(() => {
    axios.get(`${API}/campaign/config`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const tierList = cfg?.tiers || [];
  const currentTier = useMemo(
    () => tierList.find((t) => t.id === form.wineTier) || null,
    [tierList, form.wineTier],
  );
  const halfAllowed = cfg?.halfBatchAllowedWines || [];
  const bottleAddonPer = cfg?.bottleAddonPerBottle ?? 2;
  const discount = cfg?.discount ?? 15;

  // Auto-derive wineCategory from wineTier (fruit tier → fruit; otherwise keep user choice)
  useEffect(() => {
    if (form.wineTier === 'fruit' && form.wineCategory !== 'fruit') {
      setForm((p) => ({ ...p, wineCategory: 'fruit' }));
    }
  }, [form.wineTier, form.wineCategory]);

  // Half-batch (15) only allowed for silver + approved wines; if user picks
  // 15 with an incompatible tier/wine, auto-reset wineName so they must pick again.
  const wineOptions = currentTier?.wines || [];
  const filteredWines = useMemo(() => {
    if (form.bottleQuantity !== 15) return wineOptions;
    return wineOptions.filter((w) => w.halfEligible);
  }, [wineOptions, form.bottleQuantity]);

  useEffect(() => {
    if (form.wineName && !filteredWines.find((w) => w.name === form.wineName)) {
      setForm((p) => ({ ...p, wineName: '' }));
    }
  }, [filteredWines, form.wineName]);

  // Live pricing preview
  const pricing = useMemo(() => {
    if (!currentTier) return null;
    let winePrice = 0;
    if (form.bottleQuantity === 30) {
      winePrice = currentTier.fullPrice;
    } else {
      // 15 bottles: only valid if wine is half-batch eligible
      const w = wineOptions.find((x) => x.name === form.wineName);
      if (w && w.halfEligible) winePrice = currentTier.halfPrice;
      else winePrice = null; // invalid selection
    }
    if (winePrice == null) {
      return { invalid: true, wine: 0, bottles: 0, subtotal: 0, discount: 0, promo: 0 };
    }
    const bottles = form.includeBottles ? bottleAddonPer * form.bottleQuantity : 0;
    const subtotal = winePrice + bottles;
    const discountAmount = subtotal * (discount / 100);
    return {
      invalid: false,
      wine: winePrice,
      bottles,
      subtotal,
      discount: discountAmount,
      promo: subtotal - discountAmount,
    };
  }, [currentTier, form.bottleQuantity, form.wineName, form.includeBottles, wineOptions, bottleAddonPer, discount]);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const validateStep1 = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    const digits = form.phone.replace(/\D/g, '');
    if (digits.length < 10) e.phone = 'Enter a valid phone number';
    if (!form.ageConfirmed) e.ageConfirmed = 'You must confirm you are 19+';
    if (!form.contactConsent) e.contactConsent = 'Contact permission is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.wineTier) e.wineTier = 'Choose a wine tier';
    if (!form.wineName) e.wineName = 'Choose a wine from the list';
    if (form.bottleQuantity === 15) {
      const w = wineOptions.find((x) => x.name === form.wineName);
      if (!w || !w.halfEligible) {
        e.wineName = 'Half batch (15) requires a wine flagged as half-eligible (Cabernet Sauvignon, Merlot, or Pinot Grigio variants). Pick one from the filtered dropdown.';
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep((s) => Math.min(3, s + 1));
    window.scrollTo(0, 0);
  };
  const goBack = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo(0, 0); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError('');
    setSubmitting(true);
    trackCta('order', 'order_submit', false);
    const utm = getUtm();
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        preferredContact: form.preferredContact,
        preferredContactTime: form.preferredContactTime || null,
        ageConfirmed: form.ageConfirmed,
        contactConsent: form.contactConsent,
        bottleQuantity: form.bottleQuantity,
        wineCategory: form.wineCategory,
        sweetness: form.sweetness,
        wineTier: form.wineTier,
        wineName: form.wineName || null,
        includeBottles: form.includeBottles,
        specificWine: form.wineName || form.specificWine || null,
        flavourNotes: form.flavourNotes || null,
        bottlesRequired: form.includeBottles,
        comments: form.comments || null,
        incomingReferralCode: form.incomingReferralCode || null,
        utmSource: params.get('utm_source') || utm.utm_source || null,
        utmMedium: params.get('utm_medium') || utm.utm_medium || null,
        utmCampaign: params.get('utm_campaign') || utm.utm_campaign || null,
        utmContent: params.get('utm_content') || utm.utm_content || null,
        utmTerm: params.get('utm_term') || utm.utm_term || null,
        idempotencyKey,
      };
      const { data } = await axios.post(`${API}/campaign/orders`, payload);
      if (!data?.emailConfirmed || data?.emailStatus !== 'sent') {
        throw new Error('Both required emails were not confirmed.');
      }
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem('ff_lokal_ref_in');
      sessionStorage.setItem('ff_lokal_last_order', JSON.stringify(data));
      navigate(`${basePath}/thank-you`, { state: data });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message;
      const reference = detail?.reference ? ` Reference: ${detail.reference}` : '';
      setSubmitError(`${message || err.message || 'Submission failed. Please try again.'}${reference}`);
    } finally {
      setSubmitting(false);
    }
  };

  const bottlesAddCost = bottleAddonPer * form.bottleQuantity;

  return (
    <div className="ff-section" data-testid="ff-form-page">
      <div className="ff-container ff-form-shell">
        <h1 className="ff-serif ff-text-center" style={{ marginBottom: 8 }}>Create Your Custom Wine</h1>
        <p className="ff-lede">Step {step} of 3</p>

        <div className="ff-form-progress">
          <div className={`ff-step-dot ${step >= 1 ? 'active' : ''}`} />
          <div className={`ff-step-dot ${step >= 2 ? 'active' : ''}`} />
          <div className={`ff-step-dot ${step >= 3 ? 'active' : ''}`} />
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {step === 1 && (
            <div data-testid="ff-form-step-1">
              <h2 className="ff-serif" style={{ fontSize: 22, marginTop: 0 }}>Contact Information</h2>

              <div className="ff-field">
                <label htmlFor="firstName">First name *</label>
                <input id="firstName" data-testid="ff-firstname" type="text" value={form.firstName} onChange={(e) => update('firstName', e.target.value)} required />
                {errors.firstName && <div className="ff-error" data-testid="ff-firstname-error">{errors.firstName}</div>}
              </div>

              <div className="ff-field">
                <label htmlFor="lastName">Last name *</label>
                <input id="lastName" data-testid="ff-lastname" type="text" value={form.lastName} onChange={(e) => update('lastName', e.target.value)} required />
                {errors.lastName && <div className="ff-error" data-testid="ff-lastname-error">{errors.lastName}</div>}
              </div>

              <div className="ff-field">
                <label htmlFor="email">Email *</label>
                <input id="email" data-testid="ff-email" type="email" autoComplete="email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
                {errors.email && <div className="ff-error" data-testid="ff-email-error">{errors.email}</div>}
              </div>

              <div className="ff-field">
                <label htmlFor="phone">Mobile phone number *</label>
                <input id="phone" data-testid="ff-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="e.g. 705-123-4567" required />
                {errors.phone && <div className="ff-error" data-testid="ff-phone-error">{errors.phone}</div>}
              </div>

              <div className="ff-field">
                <label>Preferred contact method *</label>
                <div className="ff-radio-group three">
                  {CONTACT_METHOD.map((opt) => (
                    <label key={opt.value} className={`ff-radio-tile ${form.preferredContact === opt.value ? 'selected' : ''}`}>
                      <input data-testid={`ff-contact-${opt.value}`} type="radio" name="preferredContact" value={opt.value} checked={form.preferredContact === opt.value} onChange={() => update('preferredContact', opt.value)} />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="ff-field">
                <label htmlFor="preferredContactTime">Preferred contact time (optional)</label>
                <input id="preferredContactTime" data-testid="ff-contact-time" type="text" placeholder="e.g. Weekdays after 5pm" value={form.preferredContactTime} onChange={(e) => update('preferredContactTime', e.target.value)} />
              </div>

              <div className="ff-checkbox-row">
                <input id="age" data-testid="ff-age-check" type="checkbox" checked={form.ageConfirmed} onChange={(e) => update('ageConfirmed', e.target.checked)} />
                <label htmlFor="age">I confirm I am 19 years of age or older.</label>
              </div>
              {errors.ageConfirmed && <div className="ff-error" data-testid="ff-age-error" style={{ marginTop: -8, marginBottom: 12 }}>{errors.ageConfirmed}</div>}

              <div className="ff-checkbox-row">
                <input id="consent" data-testid="ff-consent-check" type="checkbox" checked={form.contactConsent} onChange={(e) => update('contactConsent', e.target.checked)} />
                <label htmlFor="consent">I give permission for The Lokal to contact me about my order.</label>
              </div>
              {errors.contactConsent && <div className="ff-error" data-testid="ff-consent-error" style={{ marginTop: -8, marginBottom: 12 }}>{errors.contactConsent}</div>}

              <p style={{ fontSize: 13, color: 'var(--ff-brown-soft)', marginTop: 20 }}>
                You must be 19 years of age or older to participate. Valid government-issued identification may be required. Please enjoy responsibly.
              </p>

              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <KloneDiscoverCTA variant="chip" testId="ff-klone-chip-step1" />
              </div>

              <div className="ff-mt-6" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <button type="button" className="ff-btn ff-btn-ghost" onClick={() => navigate(`${basePath}/`)} data-testid="ff-step1-back">
                  Back
                </button>
                <button type="button" data-testid="ff-next-1" className="ff-btn ff-btn-primary" onClick={goNext}>
                  Continue
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="ff-form-step-2">
              <h2 className="ff-serif" style={{ fontSize: 22, marginTop: 0 }}>Your Wine Experience</h2>

              <div className="ff-field">
                <label>Choose your batch size *</label>
                <div className="ff-radio-group">
                  {[
                    { q: 30, label: 'Full batch · 30 bottles' },
                    { q: 15, label: 'Half batch · 15 bottles' },
                  ].map(({ q, label }) => (
                    <label key={q} className={`ff-radio-tile ${form.bottleQuantity === q ? 'selected' : ''}`} data-testid={`ff-qty-${q}`}>
                      <input data-testid={`ff-qty-${q}-input`} type="radio" name="bottleQuantity" checked={form.bottleQuantity === q} onChange={() => update('bottleQuantity', q)} />
                      <span><strong>{label}</strong> · save {discount}%</span>
                    </label>
                  ))}
                </div>
                {form.bottleQuantity === 15 && (
                  <p style={{ fontSize: 13, color: 'var(--ff-brown-soft)', margin: '8px 0 0' }}>
                    Half batches are only available for <strong>Cabernet Sauvignon</strong>, <strong>Merlot</strong>, or <strong>Pinot Grigio</strong> (Silver tier). Price = (full/2) + ${cfg?.halfBatchSurcharge ?? 18}.
                  </p>
                )}
              </div>

              <div className="ff-field">
                <label htmlFor="wineTier">Wine tier *</label>
                <select
                  id="wineTier"
                  data-testid="ff-wine-tier"
                  value={form.wineTier || ''}
                  onChange={(e) => update('wineTier', e.target.value)}
                >
                  <option value="">Choose a tier…</option>
                  {tierList.map((t) => (
                    <option
                      key={t.id}
                      value={t.id}
                      disabled={form.bottleQuantity === 15 && t.id !== HALF_BATCH_TIER}
                    >
                      {t.name} — Full ${t.fullPrice.toFixed(2)}
                      {t.id === HALF_BATCH_TIER ? ` · Half $${t.halfPrice.toFixed(2)}` : ''}
                    </option>
                  ))}
                </select>
                {errors.wineTier && <div className="ff-error" data-testid="ff-wine-tier-error">{errors.wineTier}</div>}
              </div>

              <div className="ff-field">
                <label htmlFor="wineName">Choose your wine *</label>
                <select
                  id="wineName"
                  data-testid="ff-wine-name"
                  value={form.wineName}
                  onChange={(e) => update('wineName', e.target.value)}
                  disabled={!form.wineTier}
                >
                  <option value="">
                    {form.wineTier ? 'Choose a wine…' : 'Select a tier first'}
                  </option>
                  {filteredWines.map((w) => (
                    <option key={`${w.color}-${w.name}`} value={w.name}>
                      {w.name} · {w.color === 'red' ? 'Red' : w.color === 'white' ? 'White' : 'Fruit'}
                    </option>
                  ))}
                </select>
                {errors.wineName && <div className="ff-error" data-testid="ff-wine-name-error">{errors.wineName}</div>}
                {form.bottleQuantity === 15 && filteredWines.length === 0 && form.wineTier && (
                  <p style={{ fontSize: 13, color: '#a94040', margin: '6px 0 0' }}>
                    This tier has no half-batch wines. Choose Silver tier or switch to full (30) batch.
                  </p>
                )}
              </div>

              <KloneDiscoverCTA variant="inline" testId="ff-klone-inline" />

              <div className="ff-field">
                <label htmlFor="sweetness">Sweetness preference *</label>
                <select id="sweetness" data-testid="ff-sweetness" value={form.sweetness} onChange={(e) => update('sweetness', e.target.value)}>
                  {SWEETNESS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              <div className="ff-field">
                <label>Glass bottles *</label>
                <div className="ff-radio-group">
                  <label
                    className={`ff-radio-tile ${form.includeBottles ? 'selected' : ''}`}
                    data-testid="ff-bottles-add"
                  >
                    <input
                      data-testid="ff-bottles-add-input"
                      type="radio"
                      name="includeBottles"
                      checked={form.includeBottles === true}
                      onChange={() => update('includeBottles', true)}
                    />
                    <span>
                      <strong>Add glass bottles</strong> · +${bottleAddonPer.toFixed(2)}/bottle
                      <br />
                      <small style={{ color: 'var(--ff-brown-soft)' }}>
                        (+${bottlesAddCost.toFixed(2)} for {form.bottleQuantity} bottles)
                      </small>
                    </span>
                  </label>
                  <label
                    className={`ff-radio-tile ${!form.includeBottles ? 'selected' : ''}`}
                    data-testid="ff-bottles-byo"
                  >
                    <input
                      data-testid="ff-bottles-byo-input"
                      type="radio"
                      name="includeBottles"
                      checked={form.includeBottles === false}
                      onChange={() => update('includeBottles', false)}
                    />
                    <span><strong>Bring your own bottles</strong><br />
                      <small style={{ color: 'var(--ff-brown-soft)' }}>No add-on charge</small>
                    </span>
                  </label>
                </div>
              </div>

              <div className="ff-field">
                <label htmlFor="flavourNotes">Flavour notes or preferences (optional)</label>
                <textarea id="flavourNotes" data-testid="ff-flavour-notes" rows={3} value={form.flavourNotes} onChange={(e) => update('flavourNotes', e.target.value)} placeholder="e.g. fruity, oaky, low tannin" />
              </div>

              <div className="ff-field">
                <label htmlFor="comments">Additional comments (optional)</label>
                <textarea id="comments" data-testid="ff-comments" rows={3} value={form.comments} onChange={(e) => update('comments', e.target.value)} />
              </div>

              {/* Live pricing preview */}
              {pricing && !pricing.invalid && (
                <div className="ff-confirmation-badge" style={{ margin: '16px 0' }} data-testid="ff-live-price">
                  <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--ff-brown-soft)', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Live estimate
                  </p>
                  <p style={{ margin: 0, fontSize: 14 }}>
                    Wine: ${pricing.wine.toFixed(2)}
                    {pricing.bottles > 0 && <> · Bottles: +${pricing.bottles.toFixed(2)}</>}
                    {' '}· Subtotal: ${pricing.subtotal.toFixed(2)}
                  </p>
                  <p style={{ margin: '6px 0 0', fontSize: 18, color: 'var(--ff-burgundy)' }}>
                    <strong>{discount}% off · ${pricing.promo.toFixed(2)}</strong>
                    <span style={{ marginLeft: 8, fontSize: 13, color: 'var(--ff-brown-soft)', textDecoration: 'line-through' }}>
                      ${pricing.subtotal.toFixed(2)}
                    </span>
                  </p>
                </div>
              )}

              <div className="ff-mt-6" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <button type="button" className="ff-btn ff-btn-ghost" onClick={goBack} data-testid="ff-step2-back">Back</button>
                <button type="button" data-testid="ff-next-2" className="ff-btn ff-btn-primary" onClick={goNext}>Review</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="ff-form-step-3">
              <h2 className="ff-serif" style={{ fontSize: 22, marginTop: 0 }}>Review your request</h2>

              <div className="ff-card" style={{ padding: 24 }}>
                <p style={{ margin: '0 0 8px' }}><strong>Your experience</strong></p>
                <p style={{ margin: 0 }}>
                  {form.bottleQuantity === 30 ? 'Full batch · 30 bottles' : 'Half batch · 15 bottles'}
                  {' · '}{currentTier?.name || form.wineTier}
                  {form.wineName ? ` · ${form.wineName}` : ''}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ff-brown-soft)' }}>
                  Sweetness: {SWEETNESS.find(s => s.value === form.sweetness)?.label} · Bottles: {form.includeBottles ? `The Lokal supplies (+$${bottlesAddCost.toFixed(2)})` : 'Bring your own'}
                </p>
                {form.flavourNotes && <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ff-brown-soft)' }}>Notes: {form.flavourNotes}</p>}
                {form.comments && <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ff-brown-soft)' }}>Comments: {form.comments}</p>}
              </div>

              {pricing && !pricing.invalid && (
                <div className="ff-confirmation-badge" style={{ margin: '20px 0' }}>
                  <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--ff-brown-soft)', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Pricing preview
                  </p>
                  <p style={{ margin: 0, fontSize: 14 }}>
                    Wine ({form.wineName || form.wineTier}): ${pricing.wine.toFixed(2)}
                  </p>
                  {pricing.bottles > 0 && (
                    <p style={{ margin: 0, fontSize: 14 }}>
                      Glass bottles ({form.bottleQuantity} × ${bottleAddonPer.toFixed(2)}): +${pricing.bottles.toFixed(2)}
                    </p>
                  )}
                  <p style={{ margin: '4px 0 0', fontSize: 15 }}>
                    Subtotal: <span style={{ textDecoration: 'line-through' }}>${pricing.subtotal.toFixed(2)}</span> · Discount: -${pricing.discount.toFixed(2)}
                  </p>
                  <p style={{ margin: '6px 0 0', fontSize: 20, color: 'var(--ff-burgundy)' }}>
                    <strong>{discount}% off · Estimated ${pricing.promo.toFixed(2)}</strong>
                  </p>
                  <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--ff-brown-soft)' }}>
                    Final pricing is confirmed by The Lokal team based on your wine selection.
                  </p>
                </div>
              )}

              <div className="ff-card" style={{ padding: 24 }}>
                <p style={{ margin: '0 0 8px' }}><strong>Your details</strong></p>
                <p style={{ margin: 0, fontSize: 15 }}>{form.firstName} {form.lastName}</p>
                <p style={{ margin: 0, fontSize: 15 }}>{form.email}</p>
                <p style={{ margin: 0, fontSize: 15 }}>{form.phone}</p>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ff-brown-soft)' }}>
                  Prefer {form.preferredContact}{form.preferredContactTime ? ` · ${form.preferredContactTime}` : ''}
                </p>
              </div>

              <p style={{ fontSize: 13, color: 'var(--ff-brown-soft)', marginTop: 20 }}>
                You must be 19+ to participate. Submitting sends an order request — final pricing, availability and promotional
                eligibility will be confirmed by our team before payment.
              </p>

              {submitError && (
                <div className="ff-error" role="alert" data-testid="ff-submit-error" style={{ marginTop: 16 }}>
                  {submitError}
                </div>
              )}

              <div className="ff-mt-6" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <button type="button" className="ff-btn ff-btn-ghost" onClick={goBack} disabled={submitting} data-testid="ff-step3-back">Back</button>
                <button type="submit" data-testid="ff-submit" className="ff-btn ff-btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit My Wine Experience Request'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CampaignForm;
